import React, { useEffect, useRef, useState, useCallback } from 'react';
import Video from 'twilio-video';
import { getSocket } from '../../socket';
import { FiMicOff, FiMic, FiVideo, FiPhoneCall } from 'react-icons/fi';
import { IoCall } from 'react-icons/io5';
import { FaVideo, FaVideoSlash } from 'react-icons/fa';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

export default function TwilioHandler({
  channel,
  isVideo,
  isCaller,
  receiverId,
  receiverName,
  receiverPic,
  credentials,
  token: tokenProp,
  onClose
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const roomRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(isVideo);
  const [accepted, setAccepted] = useState(!isCaller);
  const [connected, setConnected] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Timer & Status: Start ONLY when both caller has been accepted AND room connected
  const isCallConnected = isCaller ? (accepted && connected) : (accepted || connected);

  useEffect(() => {
    if (!isCallConnected) {
      setElapsedSeconds(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isCallConnected]);

  const hasInitializedRef = useRef(false);

  const handleTrackSubscribed = useCallback((track, participant) => {
    console.log('[TWILIO_AUDIO_LOG] Track subscribed:', track.kind, track.sid, 'from:', participant?.identity);
    if (participant && roomRef.current?.localParticipant && participant.identity === roomRef.current.localParticipant.identity) {
      console.log('[TWILIO_AUDIO_LOG] Ignoring local track subscription for self');
      return;
    }
    try {
      if (track.kind === 'audio') {
        const audioEl = track.attach();
        audioEl.id = `twilio-audio-${track.sid || Date.now()}`;
        audioEl.autoplay = true;
        audioEl.volume = 1.0;
        audioEl.muted = false;
        document.body.appendChild(audioEl);
        console.log('[TWILIO_AUDIO_LOG] Attached remote track directly to document body:', audioEl);
        audioEl.play().then(() => {
          console.log('[TWILIO_AUDIO_LOG] Audio playback started successfully!');
        }).catch(err => {
          console.warn('[TWILIO_AUDIO_LOG] Audio play warning:', err);
        });

        if (remoteAudioRef.current) {
          track.attach(remoteAudioRef.current);
          remoteAudioRef.current.muted = false;
          remoteAudioRef.current.volume = 1.0;
          remoteAudioRef.current.play?.().catch(e => console.log('[TWILIO_AUDIO_LOG] Ref audio play warning:', e));
        }
      } else if (track.kind === 'video') {
        if (remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
          remoteVideoRef.current.play?.().catch(e => console.log('[TWILIO_VIDEO_LOG] Video play error:', e));
        }
      }
    } catch (e) {
      console.warn('[TWILIO_AUDIO_LOG] track.attach error:', e);
    }
    setConnected(true);
  }, []);

  const handleParticipantConnected = useCallback((participant) => {
    console.log('[TWILIO_AUDIO_LOG] Participant connected:', participant.identity);
    setConnected(true);

    const checkPublications = (publications) => {
      if (!publications) return;
      publications.forEach(publication => {
        if (publication.track) {
          handleTrackSubscribed(publication.track, participant);
        }
      });
    };

    checkPublications(participant.tracks);
    checkPublications(participant.audioTracks);
    checkPublications(participant.videoTracks);

    participant.on('trackSubscribed', track => {
      console.log('[TWILIO_AUDIO_LOG] Participant trackSubscribed:', track.kind);
      handleTrackSubscribed(track, participant);
    });
  }, [handleTrackSubscribed]);

  const initTwilio = useCallback(async () => {
    if (roomRef.current) {
      console.log('[TWILIO_AUDIO_LOG] Already connected to room, skipping duplicate connect');
      return;
    }
    if (window.__TWILIO_ROOM__) {
      console.log('[TWILIO_AUDIO_LOG] Cleaning up previous global Twilio room');
      try { window.__TWILIO_ROOM__.disconnect(); } catch (_) {}
      window.__TWILIO_ROOM__ = null;
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    try {
      const twilioToken = credentials?.token || credentials?.jwt || tokenProp || '';
      if (!twilioToken) {
        console.warn('[TWILIO_AUDIO_LOG] Video token missing from backend response');
        return;
      }

      console.log('[TWILIO_AUDIO_LOG] Connecting to room:', channel);
      const room = await Video.connect(twilioToken, {
        name: channel,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: isVideo,
        preferredAudioCodecs: ['opus']
      });

      roomRef.current = room;
      window.__TWILIO_ROOM__ = room;
      console.log('[TWILIO_AUDIO_LOG] Connected to room SUCCESS:', room.name, 'Local participant:', room.localParticipant.identity);
      setConnected(true);

      room.on('trackSubscribed', (track) => {
        console.log('[TWILIO_AUDIO_LOG] Room level trackSubscribed event:', track.kind);
        handleTrackSubscribed(track);
      });

      room.localParticipant.tracks.forEach(publication => {
        if (publication.track) {
          publication.track.enable();
          console.log('[TWILIO_AUDIO_LOG] Local track enabled:', publication.track.kind);
          if (publication.track.kind === 'video' && localVideoRef.current) {
            localVideoRef.current.srcObject = new MediaStream([publication.track.mediaStreamTrack]);
          }
        }
      });

      if (room.localParticipant.audioTracks) {
        room.localParticipant.audioTracks.forEach(pub => {
          if (pub.track) {
            pub.track.enable();
            console.log('[TWILIO_AUDIO_LOG] Local audio track explicitly enabled:', pub.track.name);
          }
        });
      }

      room.participants.forEach(participant => {
        handleParticipantConnected(participant);
      });

      room.on('participantConnected', participant => {
        handleParticipantConnected(participant);
      });

      room.on('disconnected', (room, error) => {
        console.log('[TWILIO_AUDIO_LOG] Room disconnected:', error?.message || 'normal disconnect');
        setConnected(false);
      });

    } catch (err) {
      console.error('[TWILIO_AUDIO_LOG] Video connection error:', err);
    }
  }, [channel, credentials, isVideo, tokenProp, handleParticipantConnected]);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    initTwilio();
    return () => {
      if (roomRef.current) {
        try {
          console.log('[TWILIO_AUDIO_LOG] Cleaning up and disconnecting room on unmount');
          roomRef.current.disconnect();
          if (window.__TWILIO_ROOM__ === roomRef.current) {
            window.__TWILIO_ROOM__ = null;
          }
          roomRef.current = null;
        } catch (_) {}
      }
      const dynamicAudioEls = document.querySelectorAll('audio[id^="twilio-audio-"]');
      dynamicAudioEls.forEach(el => el.remove());
    };
  }, [initTwilio]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleCallAccepted = (data) => {
      console.log("[TWILIO_AUDIO_LOG] Call accepted signal received from receiver:", data);
      if (!data?.channel || String(data.channel) === String(channel)) {
        setAccepted(true);
        setConnected(true);
      }
    };

    const handleCallEnded = (data) => {
      console.log("[TWILIO_AUDIO_LOG] Call ended signal received:", data);
      if (!data?.channel || String(data.channel) !== String(channel)) {
        console.log("[TWILIO_AUDIO_LOG] Ignoring call ended for mismatch channel:", data?.channel);
        return;
      }
      onClose();
    };

    const handleCallRejected = (data) => {
      console.log("[TWILIO_AUDIO_LOG] Call rejected signal received:", data);
      if (accepted || connected) {
        console.log("[TWILIO_AUDIO_LOG] Ignoring call_rejected signal because call is already accepted/connected");
        return;
      }
      if (!data?.channel || String(data.channel) !== String(channel)) {
        console.log("[TWILIO_AUDIO_LOG] Ignoring call rejected for mismatch channel:", data?.channel);
        return;
      }
      onClose();
    };

    socket.on('call_accepted', handleCallAccepted);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_rejected', handleCallRejected);

    return () => {
      socket.off('call_accepted', handleCallAccepted);
      socket.off('call_ended', handleCallEnded);
      socket.off('call_rejected', handleCallRejected);
    };
  }, [channel, onClose]);

  useEffect(() => {
    if (!roomRef.current) return;
    try {
      roomRef.current.localParticipant.audioTracks.forEach(pub => {
        if (pub.track) {
          if (micOn) pub.track.enable();
          else pub.track.disable();
        }
      });
    } catch (e) {
      console.log('[TWILIO_AUDIO_LOG] Mic toggle error:', e);
    }
  }, [micOn]);

  useEffect(() => {
    if (!roomRef.current) return;
    try {
      roomRef.current.localParticipant.videoTracks.forEach(pub => {
        if (pub.track) {
          if (cameraOn) pub.track.enable();
          else pub.track.disable();
        }
      });
    } catch (e) {
      console.log('[TWILIO_AUDIO_LOG] Camera toggle error:', e);
    }
  }, [cameraOn]);

  const leaveCall = () => {
    console.log('[TWILIO_AUDIO_LOG] User clicked leaveCall');
    if (roomRef.current) {
      try {
        roomRef.current.localParticipant?.tracks?.forEach(pub => {
          if (pub.track?.stop) pub.track.stop();
        });
        roomRef.current.disconnect();
      } catch (_) {}
      roomRef.current = null;
    }
    if (window.__TWILIO_ROOM__) {
      try { window.__TWILIO_ROOM__.disconnect(); } catch (_) {}
      window.__TWILIO_ROOM__ = null;
    }
    const dynamicAudioEls = document.querySelectorAll('audio[id^="twilio-audio-"]');
    dynamicAudioEls.forEach(el => {
      try {
        el.pause();
        el.srcObject = null;
        el.remove();
      } catch (_) {}
    });
    const socket = getSocket();
    if (socket?.connected && channel) {
      socket.emit('call_end', {
        otherId: receiverId,
        channel,
        status: 'completed',
        reason: 'ended_by_user'
      });
    }
    onClose();
  };

  const statusText = isCallConnected
    ? "Connected"
    : isCaller
      ? "Ringing..."
      : "Connecting...";

  const controlButtonClass = "flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl transition duration-200 hover:-translate-y-0.5";

  if (isVideo) {
    return (
      <div className="relative h-[min(720px,92vh)] min-h-[600px] w-full overflow-hidden rounded-[28px] bg-black text-white shadow-2xl">
        <audio ref={remoteAudioRef} autoPlay playsInline controls={false} className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-none" />
        <div className="absolute inset-0 z-0 bg-gray-950 flex items-center justify-center">
          <video ref={remoteVideoRef} autoPlay playsInline className={`h-full w-full object-cover ${accepted && connected ? "block" : "hidden"}`} />
          {(!accepted || !connected) && (
            <div className="flex flex-col items-center">
              <img src={receiverPic} alt={receiverName} className="h-32 w-32 rounded-full border-[4px] border-white/20 object-cover blur-sm opacity-50" />
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/35 via-transparent to-black/65" />

        <div className="absolute left-5 top-5 z-20 h-36 w-28 overflow-hidden rounded-2xl border border-white/30 bg-black/30 shadow-2xl">
          {cameraOn ? (
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900">
              <FaVideoSlash className="text-xl text-white/50" />
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-semibold backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiVideo />
            Video call (Twilio)
          </span>
        </div>

        <div className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-2xl bg-black/35 px-5 py-3 text-center shadow-xl backdrop-blur-md">
          <h2 className="m-0 max-w-[220px] truncate text-2xl font-bold">{receiverName}</h2>
          <p className="m-0 mt-1 text-sm font-semibold text-white/80">
            {accepted && connected ? formatDuration(elapsedSeconds) : statusText}
          </p>
        </div>

        <div className="absolute bottom-6 left-1/2 z-30 flex w-[calc(100%-32px)] max-w-[390px] -translate-x-1/2 items-center justify-between rounded-[28px] bg-black/55 px-8 py-5 shadow-2xl backdrop-blur-xl">
          <button
            type="button"
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`${controlButtonClass} ${micOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={() => setMicOn(!micOn)}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
            onClick={leaveCall}
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
          <button
            type="button"
            title={cameraOn ? "Turn camera off" : "Turn camera on"}
            className={`${controlButtonClass} ${cameraOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={() => setCameraOn(!cameraOn)}
          >
            {cameraOn ? <FaVideo className="text-xl" /> : <FaVideoSlash className="text-xl" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[min(660px,90vh)] min-h-[540px] w-full overflow-hidden rounded-[30px] bg-[#190028] text-white shadow-2xl">
      <audio ref={remoteAudioRef} autoPlay playsInline controls={false} className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-none" />
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-[16px]"
        style={{ backgroundImage: `url(${receiverPic})` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(171,34,255,0.95)_0%,rgba(117,21,208,0.96)_42%,rgba(37,6,58,0.98)_100%)]" />
      <div className="absolute left-1/2 top-24 h-56 w-56 -translate-x-1/2 rounded-full bg-white/12 blur-3xl" />
      <div className="absolute -left-16 bottom-20 h-56 w-56 rounded-full bg-[#ff4f8b]/20 blur-3xl" />

      <div className="relative z-[2] flex h-full flex-col items-center justify-between px-6 py-8">
        <div className="rounded-full bg-white/14 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiPhoneCall />
            Audio call (Twilio)
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-[-18px] animate-pulse rounded-full bg-white/18 blur-md" />
            <div className="absolute inset-[-34px] rounded-full border border-white/10" />
            <img
              src={receiverPic}
              alt={receiverName || "Caller"}
              className="relative h-36 w-36 rounded-full border-[5px] border-white object-cover shadow-2xl"
            />
          </div>
          <h2 className="m-0 max-w-[220px] truncate text-4xl font-bold leading-tight">{receiverName}</h2>
          <p className="m-0 mt-2 text-sm font-medium text-white/75">{statusText}</p>
          <p className="m-0 mt-4 rounded-full bg-black/22 px-5 py-2 text-sm font-semibold tracking-wide text-white/90 shadow-inner">
            {formatDuration(elapsedSeconds)}
          </p>
        </div>

        <div className="flex w-full max-w-[340px] items-center justify-between rounded-[30px] bg-black/42 px-9 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          <button
            type="button"
            onClick={() => setMicOn(!micOn)}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`group flex h-16 w-16 items-center justify-center rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-xl ${micOn ? "bg-black/70 text-white" : "bg-white text-[#190028]"}`}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            onClick={leaveCall}
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
        </div>
      </div>
    </div>
  );
}
